import { useState } from "react";
import { Plus, Search } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { ListShell } from "@/components/shared/ListShell";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import { ListPagination } from "@/components/shared/ListPagination";
import { Input } from "@/components/ui/input";
import { BusinessRow } from "@/components/businesses/BusinessRow";
import { AddFeaturedDialog } from "@/components/featured/AddFeaturedDialog";
import { useListQueryState } from "@/hooks/useListQueryState";
import { useBusinessList } from "@/hooks/useBusinesses";

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

export const FeaturedList = () => {
  const { state, patch } = useListQueryState({
    defaultLimit: DEFAULT_PAGE_SIZE,
    allowedLimits: PAGE_SIZE_OPTIONS,
  });
  const { page, limit, search } = state;
  const [searchInput, setSearchInput] = useState(search);
  const [addOpen, setAddOpen] = useState(false);

  const { data, isLoading, error, refetch } = useBusinessList({
    isFeatured: true,
    search: search || undefined,
    page,
    limit,
  });

  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const handleSearchChange = (val: string) => {
    setSearchInput(val);
    const t = setTimeout(() => patch({ search: val, resetPage: true }), 300);
    return () => clearTimeout(t);
  };

  return (
    <AppLayout title="Featured Businesses">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search featured businesses…"
            className="pl-8"
          />
        </div>
        <PermissionGate module="featured" action="edit">
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-btn bg-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add Featured Business
          </button>
        </PermissionGate>
      </div>

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={() => refetch()}
        emptyTitle="No featured businesses"
        emptyDescription="Add approved businesses to the featured list using the button above."
      >
        <div className="bg-card rounded-card border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-xs text-muted-foreground uppercase">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Business</th>
                <th className="px-3 py-2 text-left font-medium">City</th>
                <th className="px-3 py-2 text-left font-medium">Reviews</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Visible</th>
                <th className="px-3 py-2 text-left font-medium">Featured</th>
                <th className="px-3 py-2 text-left font-medium">Joined</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <BusinessRow
                  key={row.id}
                  row={row}
                  onReject={() => {}}
                  showPhone={false}
                  showSubscription={false}
                />
              ))}
            </tbody>
          </table>
        </div>

        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="featured businesses"
          onPageChange={(p) => patch({ page: p })}
          limit={limit}
          onLimitChange={(n) => patch({ limit: n, resetPage: true })}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </ListShell>

      <AddFeaturedDialog open={addOpen} onOpenChange={setAddOpen} />
    </AppLayout>
  );
};

export default FeaturedList;
